import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'

import { ClientsPage } from '@/pages/clients'
import { DriversPage } from '@/pages/drivers'
import { LoginPage } from '@/pages/login'
import { MissionDetailPage } from '@/pages/mission-detail'
import { MissionsPage } from '@/pages/missions'
import { NotFoundPage } from '@/pages/not-found'
import { PlanningPage } from '@/pages/planning'
import { VehiclesPage } from '@/pages/vehicles'
import { useAuthStore } from '@/stores/auth.store'

/**
 * Code-based router for the admin web.
 *
 * Mirror of the driver PWA's router, with two admin-specific
 * twists:
 *
 *   1. **Index redirect to `/planning`.** The driver app sends
 *      to `/dashboard`; the admin home is the planning page,
 *      which is what the gérant opens first thing every morning.
 *   2. **Role gate in the protected layout.** A driver who
 *      successfully authenticates against this app must be
 *      bounced to `/login` (which then redirects them to the
 *      driver PWA via the link in the login footer). We don't
 *      want them stuck staring at empty admin pages.
 *
 * Pathless layout route (`id: 'authed'`) carries the guard so
 * every protected page inherits it without repeating the check.
 */

/* -------------------------------------------------------------------------- */
/*  Root + public                                                              */
/* -------------------------------------------------------------------------- */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/planning' })
  },
})

interface LoginSearch {
  redirect?: string
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch =>
    typeof search.redirect === 'string'
      ? { redirect: search.redirect }
      : {},
  component: LoginPage,
})

/* -------------------------------------------------------------------------- */
/*  Protected layout                                                           */
/* -------------------------------------------------------------------------- */

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ location }) => {
    const { user, accessToken } = useAuthStore.getState()
    if (user === null || accessToken === null) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      })
    }
    // Drivers can authenticate via /auth/login but they don't
    // belong on this app — kick them back to /login. The login
    // page detects a driver session in its own effect and logs
    // them out so the next attempt isn't blocked by the same
    // guard repeatedly.
    if (user.role !== 'admin' && user.role !== 'super_admin') {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      })
    }
  },
  component: () => <Outlet />,
})

const planningRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/planning',
  component: PlanningPage,
})

const missionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/missions',
  component: MissionsPage,
})

const missionDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  // `$id` is TanStack Router's path-param syntax. Declared AFTER
  // `/missions` so the static path keeps priority on `/missions`
  // alone (matchers are positional).
  path: '/missions/$id',
  component: MissionDetailPage,
})

const driversRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/chauffeurs',
  component: DriversPage,
})

const vehiclesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/vehicules',
  component: VehiclesPage,
})

const clientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients',
  component: ClientsPage,
})

/* -------------------------------------------------------------------------- */
/*  Tree + router                                                              */
/* -------------------------------------------------------------------------- */

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  protectedRoute.addChildren([
    planningRoute,
    missionsRoute,
    missionDetailRoute,
    driversRoute,
    vehiclesRoute,
    clientsRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
